const _ = require("lodash");
const Fractions = require("../util/Fractions");
const ImageCache = require("../util/ImageCache");
const ColorUtil = require("../util/ColorUtil");
const colors = ColorUtil.getDiagramColors();
const Font = require("../util/Font");
const CanvasText = require("../util/CanvasText");
const LineLayout = require("./LineLayout");

const drawDiagram = require("./Diagram").drawDiagram;

const {
	drawArrowHead,
	calculateArrowHeadPositions,
} = require("../util/ArrowDraw");

const Helpers = require("./util/RenderHelpers");
// Alias commonly used helpers
const {
	roundValForLineWidth,
	getIntegerLineWidth,
	getLineWidth,
	getDigitWidth,
} = Helpers;

const DrawRectUtil = require("../util/DrawRectangleUtil");

//Draws a filled in triangle- should be in DrawingUtils, but causes
// circular dependency
const drawFilledTriangle = function(ctx, points) {
	ctx.save();
	ctx.beginPath();
	ctx.moveTo(points[0]);
	ctx.lineTo(points[1]);
	ctx.lineTo(points[2]);
	ctx.closePath();
	ctx.fill();
	ctx.stroke();
	ctx.restore();
};

const NodesEnvironments = {
	alignLine: {
		draw(rc, x, y, node, measure) {
			const {pieceMeasures} = measure;
			let currX = x;
			currX -= pieceMeasures[0].width;
			for (let i = 0; i < node.children.length; i++) {
				const child = node.children[i];
				//Special case to deal with missing parentheses in align equations
				if (child.children.length === 1 && rc.isBlockNode(child.children[0])) {
					rc.drawNode(
						rc,
						currX,
						y - rc.getMeasure(child.children[0]).height / 2,
						child.children[0]
					);
				} else {
					rc.drawNode(rc, currX, y, child);
				}
				currX += pieceMeasures[i].width;
			}
		},
		measure(rc, fontInfo, props, node) {
			let pieceMeasure;
			let currWidth = 0;
			const pieceMeasures = [];
			let maxTop = 0;
			let maxBottom = 0;
			let left = 0;
			for (let i = 0; i < node.children.length; i += 1) {
				pieceMeasure = rc.measureNode(rc, fontInfo, props, node.children[i]);
				if (i === 0) {
					left += pieceMeasure.width;
				}
				currWidth += pieceMeasure.width;
				pieceMeasures.push(pieceMeasure);
				maxTop = Math.max(maxTop, pieceMeasure.top);
				maxBottom = Math.max(maxBottom, pieceMeasure.bottom);
			}
			return {
				left: left,
				right: currWidth - left,
				width: currWidth,
				top: maxTop,
				bottom: maxBottom,
				fontInfo,
				pieceMeasures,
			};
		},
	},
	alignMath: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {maxLeft, scale, lineMeasures, yPadding} = measure;
			let startX = x + maxLeft;
			let currY = y;
			context.save();
			if (scale) {
				context.scale(scale, scale);
				startX = maxLeft / scale;
				currY /= scale;
			}
			for (let i = 0; i < node.children.length; i += 1) {
				currY += lineMeasures[i].top;
				rc.drawNode(rc, startX, currY, node.children[i]);
				currY += lineMeasures[i].bottom + 2 * yPadding;
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const yPadding = fontInfo.size / 6;
			let lineMeasure;
			const lineMeasures = [];
			let maxLeft = 0;
			let maxRight = 0;
			let currHeight = 0;
			for (let i = 0; i < node.children.length; i += 1) {
				lineMeasure = rc.measureNode(rc, fontInfo, props, node.children[i]);
				lineMeasures.push(lineMeasure);
				currHeight += lineMeasure.top + lineMeasure.bottom;
				maxLeft = Math.max(maxLeft, lineMeasure.left);
				maxRight = Math.max(maxRight, lineMeasure.right);
			}
			currHeight += 2 * yPadding * (node.children.length - 1);
			const width = maxLeft + maxRight;
			const scale = false;
			return {
				width: width,
				height: currHeight,
				maxLeft: maxLeft,
				maxRight: maxRight,
				fontInfo,
				yPadding,
				lineMeasures,
				scale,
			};
		},
	},
	item: {
		draw(rc, x, y, node, measure) {
			return rc.drawNode(rc, x, y - measure.firstTop, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const wrappingMeasure = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			return {
				width: wrappingMeasure.width,
				height: wrappingMeasure.height,
				top: wrappingMeasure.height / 2,
				bottom: wrappingMeasure.height / 2,
				fontInfo,
				// extra
				yPadding: wrappingMeasure.yPadding,
				firstTop: wrappingMeasure.lineMeasures[0].top,
			};
		},
	},
	itemList: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {yPadding, tabSize, itemMeasures} = measure;
			const startX = x + tabSize; //Start drawing bullet at 4 characters in, start drawing item at 6 characters in.
			let currY = y;
			context.save();
			for (let i = 0; i < node.children.length; i++) {
				if (i === 0) {
					currY += itemMeasures[i].firstTop;
				} else {
					currY += yPadding * 4;
				}
				if (node.useBullets) {
					//Draw the bullet
					context
						.bp()
						.arc(startX, currY, tabSize / 12, 0, 2 * Math.PI)
						.fill();
				}
				//Draw the item
				rc.drawNode(
					rc,
					startX + tabSize / 3,
					currY + 2 * yPadding,
					node.children[i]
				);
				currY += itemMeasures[i].top + itemMeasures[i].bottom + 2 * yPadding;
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const yPadding = fontInfo.size / 12;
			const tabSize = getDigitWidth(rc, fontInfo) * 2;
			const leftIndent = tabSize + tabSize / 3 + tabSize / 12;
			const itemProps = _.extend({}, props, {
				maxLineWidth: props.maxLineWidth && props.maxLineWidth - leftIndent,
			});

			let itemMeasure;
			const itemMeasures = [];
			let currHeight = 0;
			let maxWidth = 0;
			for (let i = 0; i < node.children.length; i++) {
				itemMeasure = rc.measureNode(rc, fontInfo, itemProps, node.children[i]);
				itemMeasures.push(itemMeasure);
				currHeight += itemMeasure.top + itemMeasure.bottom;
				maxWidth = Math.max(maxWidth, itemMeasure.width);
			}
			currHeight +=
				2 * yPadding * node.children.length +
				yPadding * 4 * (node.children.length - 1);
			maxWidth += tabSize;
			return {
				width: props.maxLineWidth || maxWidth,
				height: currHeight,
				tabSize: tabSize,
				yPadding: yPadding,
				itemMeasures: itemMeasures,
			};
		},
	},
	tableRow: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				top,
				bottom,
				lineWidth,
				xPadding,
				yPadding,
				emptyRow,
				columnEnds,
				columnWidths,
				cellMeasures,
				fontInfo,
			} = measure;
			const vAlign = node.extraOptions.vAlign;
			// helper function to check if all descendants are block-type
			const isAllBlock = function(parent) {
				if (parent.children) {
					for (const child of parent.children) {
						if (_.isArray(child.children) && child.children.length > 0) {
							// if has children, check them instead
							if (!isAllBlock(child)) {
								return false;
							}
						} else if (child.type) {
							// if no children, check if this node is block-type
							const isBlock = rc.isBlockNode(child);
							if (!isBlock) {
								return false;
							}
						}
					}
				}
				return true;
			};

			//drawing the horizontal lines before this row
			let topY = y - top + lineWidth - yPadding;
			if (node.firstRow) {
				topY -= lineWidth;
			}
			if (emptyRow) {
				topY += yPadding;
			}
			let roundedY;
			let i;
			for (i = 0; i < node.horizontalLines.length; i++) {
				const hlineWidth = lineWidth * node.horizontalLines[i].widthMultiplier;
				context.lineWidth = hlineWidth;
				roundedY = roundValForLineWidth(topY + hlineWidth / 2, hlineWidth);
				if (node.horizontalLines[i].type === "h") {
					context
						.bp()
						.M(x, roundedY)
						.L(x + columnEnds[columnEnds.length - 1], roundedY)
						.stroke();
				} else {
					context
						.bp()
						.M(x + columnEnds[node.horizontalLines[i].start - 1], roundedY)
						.L(x + columnEnds[node.horizontalLines[i].end], roundedY)
						.stroke();
				}
				topY += 2 * hlineWidth;
			}
			context.lineWidth = lineWidth;
			topY += yPadding;
			//drawing the cells
			let currX = x;
			let drawY;
			rc.pushLineY(y, top, bottom, fontInfo);
			for (i = 0; i < node.children.length; i++) {
				if (node.tableInfo.verticalLines[i]) {
					currX += lineWidth * (2 * node.tableInfo.verticalLines[i]);
					if (i !== 0) {
						currX += lineWidth;
					}
				}
				let leftX = currX + xPadding;
				switch (node.tableInfo.columnAlignment[i]) {
					case "r":
						leftX += columnWidths[i] - cellMeasures[i].width;
						break;
					case "c":
						leftX += (columnWidths[i] - cellMeasures[i].width) / 2;
						break;
					case "l":
						break;
				}
				switch (vAlign) {
					case "t":
						drawY = y - top + cellMeasures[i].top;
						break;
					case "b":
						drawY = y + bottom - cellMeasures[i].bottom;
						break;
					default:
						drawY = y;
				}
				const child = node.children[i];
				const isBlock = isAllBlock(child);
				rc.drawNode(rc, leftX, isBlock ? topY : drawY, child);
				currX += columnWidths[i] + 2 * xPadding;
			}
			rc.popLineY();
		},
		measure(rc, fontInfo, props, node) {
			const lineWidth = getIntegerLineWidth(rc, fontInfo);
			let cellMeasure;
			let currWidth = 0;
			const cellMeasures = [];
			let maxTop = 0;
			let maxBottom = 0;
			let emptyRow = false;

			for (let i = 0; i < node.children.length; i += 1) {
				cellMeasure = rc.measureNode(rc, fontInfo, props, node.children[i]);
				currWidth += cellMeasure.width;
				cellMeasures.push(cellMeasure);
				maxTop = Math.max(maxTop, cellMeasure.top);
				maxBottom = Math.max(maxBottom, cellMeasure.bottom);
			}
			if (maxTop === 0 && maxBottom === 0) {
				emptyRow = true;
			}
			for (let i = 0; i < node.horizontalLines.length; i++) {
				maxTop += 2 * node.horizontalLines[i].widthMultiplier * lineWidth;
			}
			if (node.horizontalLines.length && !node.firstRow) {
				maxTop += lineWidth;
			}
			return {
				width: currWidth,
				top: maxTop,
				bottom: maxBottom,
				fontInfo,
				emptyRow,
				cellMeasures,
				lineWidth,
			};
		},
	},
	table: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				lineWidth,
				columnWidths,
				xPadding,
				yPadding,
				height,
				rowMeasures,
			} = measure;
			const startX = x;
			let currY = y;
			let i;

			context.save();
			context.lineWidth = lineWidth;
			//drawing the vertical lines
			let currX = startX;
			context.beginPath();
			for (i = 0; i <= columnWidths.length; i++) {
				if (i > 0) {
					currX += xPadding;
				}
				if (node.tableInfo.verticalLines[i]) {
					if (i > 0) {
						currX += lineWidth;
					}
					//Using rounding for the y to make it match up with possible horizontal lines at the top
					const startLineY = roundValForLineWidth(
						currY + lineWidth / 2,
						lineWidth
					);
					const endLineY =
						roundValForLineWidth(currY + height - 2 * lineWidth, lineWidth) +
						lineWidth / 2;
					for (let j = 0; j < node.tableInfo.verticalLines[i]; j++) {
						const roundedX = roundValForLineWidth(
							currX + lineWidth / 2,
							lineWidth
						);
						context.moveTo(roundedX, startLineY);
						context.lineTo(roundedX, endLineY);
						currX += 2 * lineWidth;
					}
				}
				if (i < columnWidths.length) {
					currX += columnWidths[i] + xPadding;
				}
			}
			context.stroke();
			//drawing the contents of the table
			for (i = 0; i < node.children.length; i += 1) {
				if (!rowMeasures[i].emptyRow) {
					currY += yPadding;
				}
				currY += rowMeasures[i].top;
				currY += measure.fontInfo.size * node.children[i].extraYSpace;
				if (node.extraOptions.mismo && i % 2 === 0) {
					currY += yPadding * 0.75;
				}
				rc.drawNode(rc, startX, currY, node.children[i]);
				if (node.extraOptions.mismo && i % 2 === 0) {
					currY -= yPadding * 0.75;
				}
				currY += rowMeasures[i].bottom;
				if (!rowMeasures[i].emptyRow) {
					currY += yPadding;
				}
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const lineWidth = getIntegerLineWidth(rc, fontInfo);
			let yPadding = fontInfo.size / 12;
			let xPadding = fontInfo.size / 6;
			if (
				node.extraOptions &&
				(node.extraOptions.xPadding || node.extraOptions.xPadding === 0)
			) {
				xPadding = fontInfo.size / 12;
			}

			if (node.extraOptions.mismo) {
				xPadding = 0;
				yPadding = fontInfo.size / 8;
			}

			const columnEnds = [];
			const numColumns = node.tableInfo.columnAlignment.length;
			const maxColumnWidths = [];
			let i;
			for (i = 0; i < numColumns; i++) {
				maxColumnWidths[i] = 0;
			}
			let rowMeasure;
			const rowMeasures = [];
			let currHeight = 0;
			for (i = 0; i < node.children.length; i += 1) {
				rowMeasure = rc.measureNode(rc, fontInfo, props, node.children[i]);
				rowMeasures.push(rowMeasure);
				//giving the column width information to the row
				rowMeasure.columnEnds = columnEnds;
				rowMeasure.columnWidths = maxColumnWidths;
				rowMeasure.xPadding = xPadding;
				rowMeasure.yPadding = yPadding;
				//calculating its height and widths
				currHeight += rowMeasure.top + rowMeasure.bottom;
				for (let j = 0; j < rowMeasure.cellMeasures.length; j++) {
					if (j >= numColumns) {
						continue; // Row has too many columns; parser already flagged error
					}
					maxColumnWidths[j] = Math.max(
						maxColumnWidths[j],
						rowMeasure.cellMeasures[j].width
					);
				}
				if (!rowMeasure.emptyRow) {
					currHeight += yPadding * 2;
				}
				currHeight += fontInfo.size * node.children[i].extraYSpace;
				//TODO deal with hlines and padding between rows
			}
			columnEnds.push(0);

			if (node.extraOptions.mismo) {
				_.fill(maxColumnWidths, _.max(maxColumnWidths));
			}

			let width = 0;
			for (i = 0; i < numColumns; i++) {
				//computing the total width
				if (node.tableInfo.verticalLines[i]) {
					width += lineWidth * (2 * node.tableInfo.verticalLines[i]);
					if (i !== 0) {
						width += lineWidth;
					}
				}
				width += maxColumnWidths[i] + 2 * xPadding;
				// columnEnds.push(width + xPadding + lineWidth);
				columnEnds.push(width + lineWidth);
			}
			if (node.tableInfo.verticalLines[numColumns]) {
				width += lineWidth * (2 * node.tableInfo.verticalLines[numColumns]);
			}
			columnEnds.push(width);
			return {
				width: width,
				height: currHeight,
				fontInfo,
				columnWidths: maxColumnWidths,
				columnEnds,
				rowMeasures,
				lineWidth,
				xPadding,
				yPadding,
			};
		},
	},
	optreeRow: {
		draw(rc, x, y, node, measure) {
			const {cellMeasures} = measure;

			for (let i = 0; i < node.children.length; i++) {
				rc.drawNode(
					rc,
					x + node.currMidPoints[i] - cellMeasures[i].width / 2,
					y,
					node.children[i]
				);
			}
		},
		measure(rc, fontInfo, props, node) {
			let currWidth = 0;
			const cellMeasures = [];
			let maxTop = 0;
			let maxBottom = 0;

			node.children.forEach(child => {
				const cellMeasure = rc.measureNode(rc, fontInfo, props, child);
				cellMeasures.push(cellMeasure);

				currWidth += cellMeasure.width;
				maxTop = Math.max(maxTop, cellMeasure.top);
				maxBottom = Math.max(maxBottom, cellMeasure.bottom);
			});

			function getNextMidPoints(prevMidPoints, rowWidth) {
				const currMidPoints = Array(node.children.length);
				_.fill(currMidPoints, false);

				if (node.extraOptions.center) {
					node.connectList.sort((con1, con2) => con1.currCol - con2.currCol);
					// Center means make a current cell's location the average of the locations of the
					// cells from the previous row that \connect to it
					if (prevMidPoints.length) {
						node.connectList.forEach(connect => {
							if (connect.type !== "error") {
								currMidPoints[connect.currCol] = 0;
								connect.prevCols.forEach(prevCol => {
									currMidPoints[connect.currCol] += prevMidPoints[prevCol];
								});
								currMidPoints[connect.currCol] /= connect.prevCols.length;
							}
						});
					}
					// Space intermediate nodes evenly in-between cells anchored with \connect's
					for (
						let i = 0, prevAnchor = 0, pendingPlacement = -1, pendingWidth = 0;
						i <= currMidPoints.length;
						i++
					) {
						// pendingPlacement is the first unanchored node in a sequence of unanchored nodes
						if (i < currMidPoints.length && currMidPoints[i] === false) {
							if (pendingPlacement === -1) {
								pendingPlacement = i;
							}
							pendingWidth += cellMeasures[i].width;
						} else {
							let currAnchor;
							if (i === currMidPoints.length) {
								currAnchor = rowWidth;
							} else {
								currAnchor = currMidPoints[i] - cellMeasures[i].width / 2;
							}
							// If unanchored nodes have not been placed yet, then place them
							if (pendingPlacement !== -1) {
								let currX = prevAnchor;
								// Extra space / (pending nodes + 1) is the padding: in-between each node, left of the first node, and right of the last node.
								const padding =
									(currAnchor - prevAnchor - pendingWidth) /
									(i - pendingPlacement + 1);
								for (let j = pendingPlacement; j < i; j++) {
									currX += padding;
									currMidPoints[j] = currX + cellMeasures[j].width / 2;
									currX += cellMeasures[j].width;
								}
								pendingWidth = 0;
								pendingPlacement = -1;
							}
							if (i !== currMidPoints.length) {
								prevAnchor = currAnchor + cellMeasures[i].width;
							}
						}
					}
				} else {
					let currX = 0;

					for (let i = 0; i < node.children.length; i++) {
						currMidPoints[i] = currX + cellMeasures[i].width / 2;
						currX += cellMeasures[i].width;
					}
				}

				return currMidPoints;
			}

			return {
				width: currWidth,
				top: maxTop,
				bottom: maxBottom,
				fontInfo,
				cellMeasures,
				getNextMidPoints,
			};
		},
	},
	optree: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const {
				rowMeasures,
				width,
				rowPadding,
				equalsPadding,
				fontInfo,
				topConnectionPadding,
				bottomConnectionPadding,
			} = measure;

			let currY = y;
			let prevMidPoints = [];
			let prevRowX = -1;

			for (let i = 0; i < node.children.length; i += 1) {
				// Left padding to center row
				let leftPadding = (width - equalsPadding - rowMeasures[i].width) / 2;
				if (node.extraOptions.center || node.extraOptions.align === "left") {
					leftPadding = 0;
				} else if (node.extraOptions.align === "right") {
					leftPadding *= 2;
				}

				// (rowX, currY) is the top-left corner of the current optree row
				currY += i === 0 ? rowPadding / 2 : rowPadding;
				const rowX = x + equalsPadding + leftPadding;

				const currMidPoints = rowMeasures[i].getNextMidPoints(
					prevMidPoints,
					width - equalsPadding
				);
				node.children[i].currMidPoints = currMidPoints;

				const ctx = rc.context;

				ctx.save();
				ctx.lineWidth = getLineWidth(rc, fontInfo);
				ctx.lineCap = "round";
				ctx.font = Font.fromInfo(fontInfo);

				// Draw connections between previous row and current row
				node.children[i].connectList.forEach(connect => {
					if (connect.type !== "error" && !connect.hidden) {
						const currCol = connect.currCol;
						const useBottomConPadding = !node.children[i].ignorePadding[
							currCol
						];
						connect.prevCols.forEach(prevCol => {
							const useTopConPadding = !node.children[i - 1].ignorePadding[
								prevCol
							];
							ctx.beginPath();
							ctx.moveTo(
								prevRowX + prevMidPoints[prevCol],
								currY -
									rowPadding +
									(useTopConPadding ? topConnectionPadding : 0)
							);
							ctx.lineTo(
								rowX + currMidPoints[currCol],
								currY - (useBottomConPadding ? bottomConnectionPadding : 0)
							);
							ctx.stroke();
						});
					}
				});

				if (i !== 0) {
					ctx.fillText("=", x, currY + rowMeasures[i].top + fontInfo.size / 3);
				}

				ctx.restore();

				currY += rowMeasures[i].top;
				rc.drawNode(rc, rowX, currY, node.children[i]);
				currY += rowMeasures[i].bottom;

				prevMidPoints = currMidPoints;
				prevRowX = rowX;
			}
		},
		measure(rc, fontInfo, props, node) {
			const rowPadding = fontInfo.size * 1.1;
			const topConnectionPadding = fontInfo.size / 10;
			const bottomConnectionPadding = fontInfo.size / 5;
			const equalsPadding = fontInfo.size;

			const rowMeasures = [];
			let currHeight = rowPadding / 2;
			let maxWidth = 0;

			for (let i = 0; i < node.children.length; i += 1) {
				const rowMeasure = rc.measureNode(
					rc,
					fontInfo,
					props,
					node.children[i]
				);
				rowMeasures.push(rowMeasure);

				currHeight +=
					rowMeasure.top +
					rowMeasure.bottom +
					(i === node.children.length - 1 ? rowPadding / 2 : rowPadding);
				maxWidth = Math.max(maxWidth, rowMeasure.width);
			}

			return {
				width: equalsPadding + maxWidth,
				height: currHeight,
				rowMeasures,
				fontInfo,
				rowPadding,
				equalsPadding,
				topConnectionPadding,
				bottomConnectionPadding,
			};
		},
	},
	mistake: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const mistakeColor = colors.orange; //This is also in mistake-icon.svg
			const context = rc.context;
			const {fontInfo, width, textMeasure, height} = measure;
			const lineWidth = getLineWidth(rc, fontInfo) * 6;
			const triangleWidth = fontInfo.size;
			const pad = fontInfo.size / 4;
			const mistakeIcon = ImageCache.src(
				global.BASE_URL + "assets/images/problem-art/mistake-icon.svg",
				true
			);

			context.save();
			context.fillStyle = colors.orange;
			context.lineWidth *= 3;
			context.font = Font.fromInfo(fontInfo);
			const xStart = x + lineWidth + pad;
			const xEnd = x + width - lineWidth - pad;
			context.drawImage(
				mistakeIcon,
				xStart,
				y + triangleWidth,
				triangleWidth,
				triangleWidth
			);
			context.drawImage(
				mistakeIcon,
				xEnd - triangleWidth,
				y + triangleWidth,
				triangleWidth,
				triangleWidth
			);
			context.font = Font.makeBold(Font.fromInfo(fontInfo));
			CanvasText.drawMiddleText(
				context,
				"Common Mistake",
				(xStart + xEnd - textMeasure.width) / 2,
				y + 4 * pad + fontInfo.size / 2
			);
			DrawRectUtil.drawRoundedRect(
				context,
				x + lineWidth / 6,
				y + lineWidth / 2,
				width - lineWidth / 3,
				height - lineWidth,
				3
			);
			context.stroke(mistakeColor);
			context.restore();

			rc.drawNode(rc, xStart, y + fontInfo.size + 8 * pad, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const lineWidth = getLineWidth(rc, fontInfo) * 6;
			const pad = fontInfo.size / 4;
			context.font = Font.makeBold(Font.fromInfo(fontInfo));
			const textMeasure = context.measureText("Common Mistake");
			const contentMeasure = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			return {
				width:
					Math.max(
						contentMeasure.width,
						textMeasure.width + 2 * fontInfo.size + 2 * pad
					) +
					4 * lineWidth,
				height: lineWidth + 8 * pad + fontInfo.size + contentMeasure.height,
				fontInfo,
				textMeasure,
			};
		},
	},
	exampleBlock: {
		draw(rc, x, y, node, measure) {
			const {
				fontInfo,
				top,
				bottom,
				text,
				pad,
				spacing,
				textMeasure,
				contentMeasure,
				contentIsBlock,
			} = measure;
			const height = top + bottom;
			const context = rc.context;

			context.save();
			context.font = Font.fromInfo(fontInfo);
			rc.drawNode(rc, x, y, text);
			context
				.bp()
				.M(textMeasure.width + pad + x, y - top)
				.v(height)
				.stroke(colors.defaultblack);
			context.restore();
			if (contentIsBlock) {
				LineLayout.drawInline(
					rc,
					x + textMeasure.width + spacing,
					y,
					node.children[0].children,
					{blocksInLine: true}
				);
			} else {
				rc.drawNode(
					rc,
					x + textMeasure.width + spacing,
					y - contentMeasure.height / 2,
					node.children[0]
				);
			}
		},
		measure(rc, fontInfo, props, node) {
			const pad = fontInfo.size / 2;
			const text = {type: "bold", children: [{type: "text", text: "EXAMPLE"}]};
			// space between example line and content
			const spacing = 2 * pad + getLineWidth(rc, fontInfo);
			const textMeasure = rc.measureNode(rc, fontInfo, props, text);
			const contentIsBlock = rc.isBlockNode(node.children[0].children[0]);
			const contentMeasure = rc.measureNode(
				rc,
				fontInfo,
				_.extend({}, props, {
					maxLineWidth: props.maxLineWidth - textMeasure.width - spacing,
				}),
				node.children[0]
			);
			const height = Math.max(
				contentMeasure.height,
				(textMeasure.top + textMeasure.bottom) * 1.5
			);
			return {
				width: textMeasure.width + contentMeasure.width + spacing,
				top: height / 2,
				bottom: height / 2,
				fontInfo,
				textMeasure,
				contentMeasure,
				contentIsBlock,
				text,
				pad,
				spacing,
			};
		},
	},
	numberline: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const info = node.numberlineInfo;
			const {
				fontInfo,
				top,
				bottom,
				sidePadding,
				arrowLabelMeasures,
				arrowEndWidth,
				arrLabelPadding,
				measurelineXMeasures,
				xWidth,
				checkWidth,
				xcheckHeight,
				maxMarkSize,
				markMeasures,
				dotMeasures,
				shadingMeasures,
				arrowMeasures,
				labelMeasures,
				markSizes,
				bigArrowMeasures,
				xMeasures,
				checkMeasures,
				measureLineMeasures,
				toAdd,
				tickMeasures,
			} = measure;
			const context = rc.context;
			context.save();
			context.lineWidth = getLineWidth(rc, fontInfo);
			const lineY = y + top;
			const lineX = x + sidePadding;
			const width = measure.width - 2 * sidePadding;
			//Drawing the line
			context
				.bp()
				.M(lineX, lineY)
				.h(width)
				.stroke();
			//Drawing the end arrows, if there are any
			if (info.endArrow === "left" || info.endArrow === "both") {
				drawArrowHead(context, {
					x1: lineX + arrowEndWidth / 2,
					x2: lineX + arrowEndWidth / 2,
					y1: lineY + arrowEndWidth / 6,
					y2: lineY - arrowEndWidth / 6,
					length: arrowEndWidth / 2,
					curved: true,
				});
			}
			if (info.endArrow === "right" || info.endArrow === "both") {
				drawArrowHead(context, {
					x1: lineX + width - arrowEndWidth / 2,
					x2: lineX + width - arrowEndWidth / 2,
					y1: lineY - arrowEndWidth / 6,
					y2: lineY + arrowEndWidth / 6,
					length: arrowEndWidth / 2,
					curved: true,
				});
			}

			//Drawing the marks
			for (let i = 0; i < info.marks.length; i++) {
				const markInfo = info.marks[i].markInfo;
				context.save();

				if (markInfo.color) {
					context.strokeStyle = colors[markInfo.color];
				}
				if (markInfo.bold) {
					context.lineWidth *= 2;
				}
				context.bp().M(markMeasures[i] + x, y);
				if (markInfo.side === "both" || markInfo.side === "top") {
					context
						.M(markMeasures[i] + x, lineY - markSizes[markInfo.size] / 2)
						.V(lineY);
				}
				if (markInfo.side === "both" || markInfo.side === "bottom") {
					context
						.M(markMeasures[i] + x, lineY + markSizes[markInfo.size] / 2)
						.V(lineY);
				}
				if (markInfo.color === null) {
					context.stroke();
				} else {
					context.stroke(markInfo.color);
				}
				context.restore();
			}
			//Drawing the dots
			_.each(info.dots, (dot, i) => {
				context.save();
				context
					.bp()
					.arc(
						dotMeasures[i] + x,
						lineY,
						(markSizes.small / 2) * dot.size,
						0,
						2 * Math.PI
					);
				if (dot.color === null) {
					context.fill();
				} else {
					context.fill(dot.color);
				}
				context.restore();
			});

			//Drawing any shading
			for (let i = 0; i < info.shading.length; i++) {
				const shadeXs = shadingMeasures[i].map(e => e + x);
				context.save();

				context.globalAlpha = 0.5;
				context.bp();
				if (info.shading[i].side === "both" || info.shading[i].side === "top") {
					context.rect(
						shadeXs[0],
						lineY - (markSizes.small * 1) / 2,
						shadeXs[1] - shadeXs[0],
						markSizes.small / 2
					);
				}
				if (
					info.shading[i].side === "both" ||
					info.shading[i].side === "bottom"
				) {
					context.rect(
						shadeXs[0],
						lineY,
						shadeXs[1] - shadeXs[0],
						markSizes.small / 2
					);
				}
				const color = ColorUtil.translateToDiagramColor(info.shading[i].color);
				if (color === null) {
					context.fill();
				} else {
					context.fill(color);
				}
				context.restore();
			}
			const arrowPadding = context.lineWidth * 2;
			//Drawing any curved arrows
			_.each(info.arrows, (arr, i) => {
				const arrowXs = arrowMeasures[i].map(d => d + x);
				if (arrowXs[0] < arrowXs[1]) {
					arrowXs[0] += arrowPadding;
					arrowXs[1] -= arrowPadding;
				} else {
					arrowXs[1] += arrowPadding;
					arrowXs[0] -= arrowPadding;
				}
				const arrowHeight = markSizes[arr.size];

				//drawing the arrow's tail
				let arrowMainY = lineY - arrowPadding;
				let arrowTopY = arrowMainY - arrowHeight;
				if (arr.side === "bottom") {
					arrowMainY = lineY + arrowPadding;
					arrowTopY = arrowMainY + arrowHeight;
				}
				if (arr.farOut && arr.farOut !== "low") {
					arrowMainY += toAdd[i];
					arrowTopY += toAdd[i];
				}
				context
					.bp()
					.M(arrowXs[0], arrowMainY)
					.Q((arrowXs[0] + arrowXs[1]) / 2, arrowTopY, arrowXs[1], arrowMainY)
					.stroke();
				if (arr.label) {
					const tempX =
						(arrowXs[0] + arrowXs[1]) / 2 -
						measure.arrowLabelMeasures[i].width / 2;
					const tempY =
						arr.side === "bottom"
							? arrLabelPadding + arrowLabelMeasures[i].top
							: -1 * arrLabelPadding - arrowLabelMeasures[i].bottom;
					rc.drawNode(
						rc,
						tempX,
						(arrowTopY + arrowMainY) / 2 + tempY,
						arr.label
					);
				}

				//drawing the arrow's head
				const headLength = markSizes.medium / 2;
				const headHeight = (headLength * 2) / 3;
				const arrowPositions = calculateArrowHeadPositions({
					startX: (arrowXs[0] + arrowXs[1]) / 2,
					endX: arrowXs[1],
					startY:
						arr.side === "bottom"
							? arrowTopY - headHeight
							: arrowTopY + headHeight,
					endY: arrowMainY,
					headLength: headLength,
					headHeight: headHeight,
				});
				drawArrowHead(context, _.extend(arrowPositions, {curved: true}));
			});
			const bigHeadLength = (markSizes.large * 3) / 4;
			const bigHeadHeight = (bigHeadLength * 2) / 3;
			const smallHeadLength = (markSizes.small * 3) / 4;
			const smallHeadHeight = (smallHeadLength * 2) / 3;
			const tinyHeadLength = (markSizes.tiny * 3) / 4;
			const tinyHeadHeight = (tinyHeadLength * 2) / 3;
			//Drawing any labels
			for (let i = 0; i < info.labels.length; i++) {
				const label = info.labels[i];
				let labelY = lineY;
				let arrowYs = [];

				let arrowHeight = 0;
				let arrowHeightWithPadding = 0;
				let arrowLinePad = 0; // So you can't see the line protrude past the arrow head
				let headLength = 0;
				let headHeight = 0;

				let arrowNumberlinePadding = markSizes.large * 0.6;
				if (label.labelInfo.close) {
					arrowNumberlinePadding *= 0.65;
				} else if (label.labelInfo.arrow === "tiny") {
					arrowNumberlinePadding *= 0.5;
				}
				if (label.labelInfo.arrow) {
					arrowHeight = measure.arrowHeight;
					if (label.labelInfo.arrow === "big") {
						arrowLinePad = bigHeadHeight / 3;
						headLength = bigHeadLength;
						headHeight = bigHeadHeight;
					} else if (label.labelInfo.arrow === "small") {
						arrowLinePad = bigHeadHeight / 3;
						headLength = smallHeadLength;
						headHeight = smallHeadHeight;
					} else if (label.labelInfo.arrow === "tiny") {
						arrowLinePad = bigHeadHeight / 3;
						headLength = tinyHeadLength;
						headHeight = tinyHeadHeight;
						label.labelInfo.arrowHead = true;
					}
					if (label.labelInfo.arrowHead) {
						arrowHeight = headHeight;
						// 1.75 to account for the left & right tails of the arrow head
						if (label.labelInfo.arrow === "tiny") {
							arrowHeightWithPadding = arrowHeight * 0.6 + measure.arrowPadding;
						} else {
							arrowHeightWithPadding =
								arrowHeight * 1.75 + measure.arrowPadding;
						}
					} else {
						// No tails since there's a line "-" with the arrow "->"
						arrowHeightWithPadding = arrowHeight + measure.arrowPadding;
					}
				}

				if (label.labelInfo.side === "top") {
					labelY -= arrowNumberlinePadding * 0.8 + measure.maxTopLabelBottom;
					arrowYs = [
						lineY - arrowNumberlinePadding - arrowHeight,
						lineY - arrowNumberlinePadding,
					];
					labelY -= arrowHeightWithPadding;
				} else if (label.labelInfo.side === "stack") {
					labelY -=
						maxMarkSize / 2 +
						measure.maxTopLabelHeight +
						measure.maxTopStackLabelBottom;
				} else {
					labelY += arrowNumberlinePadding * 0.8 + measure.maxBottomLabelTop;
					arrowYs = [
						lineY + arrowNumberlinePadding + arrowHeight,
						lineY + arrowNumberlinePadding,
					];
					labelY += arrowHeightWithPadding;
					arrowLinePad *= -1; // Swap direction of line length, so it's negative when arrowYs[1] - arrowYs[0] is also negative
				}
				rc.drawNode(rc, labelMeasures[i][0] + x, labelY, label.labelNode);

				if (label.labelInfo.arrow) {
					const arrowX = labelMeasures[i][0] + x + labelMeasures[i][1];
					const arrowPositions = calculateArrowHeadPositions({
						startX: arrowX,
						endX: arrowX,
						startY: arrowYs[0],
						endY: arrowYs[1],
						headLength: headLength,
						headHeight: headHeight,
					});
					if (label.labelInfo.arrow === "tiny") {
						const point1 = [arrowX - headLength / 2, arrowYs[0]];
						const point2 = [arrowX + headLength / 2, arrowYs[0]];
						const point3 = [arrowX, arrowYs[1]];
						drawFilledTriangle(context, [point1, point3, point2]);
					} else {
						drawArrowHead(context, _.extend(arrowPositions, {curved: true}));
					}
					if (!label.labelInfo.arrowHead) {
						context.fillRect(
							arrowX - headHeight / 6,
							arrowYs[0],
							headHeight / 3,
							arrowYs[1] - arrowYs[0] - arrowLinePad
						);
					}
				}
			}
			//Drawing any bigArrows
			for (let i = 0; i < info.bigArrows.length; i++) {
				let bigArrowYs = [
					lineY + markSizes.large / 2 + measure.arrowHeight,
					lineY + markSizes.large / 2,
				];
				if (info.bigArrows[i].side === "top") {
					bigArrowYs = [
						lineY - markSizes.large / 2 - measure.arrowHeight,
						lineY - markSizes.large / 2,
					];
				}
				const bigArrowPositionInfo = calculateArrowHeadPositions({
					startX: bigArrowMeasures[i] + x,
					endX: bigArrowMeasures[i] + x,
					startY: bigArrowYs[0],
					endY: bigArrowYs[1],
					headLength: bigHeadLength,
					headHeight: bigHeadHeight,
				});
				drawArrowHead(context, _.extend(bigArrowPositionInfo, {curved: true}));
				context.fillRect(
					bigArrowMeasures[i] + x - bigHeadHeight / 6,
					bigArrowYs[0],
					bigHeadHeight / 3,
					(bigArrowYs[1] - bigArrowYs[0]) / 2
				);
			}

			//Drawing xs and checks
			context.save();
			context.fillStyle = colors.incorrect;
			const dotSize = xcheckHeight / 5;
			_.each(xMeasures, check => {
				context.fillText("\u2717", check + x, lineY - xcheckHeight * 0.5);
				context
					.bp()
					.arc(check + x + xWidth / 2, lineY, dotSize, 0, Math.PI * 2)
					.Z()
					.fill();
			});
			context.restore();
			context.save();
			context.fillStyle = colors.correct;
			_.each(checkMeasures, check => {
				context.fillText("\u2713", check + x, lineY - xcheckHeight * 0.5);
				context
					.bp()
					.arc(check + x + checkWidth / 2, lineY, dotSize, 0, Math.PI * 2)
					.Z()
					.fill();
			});
			context.restore();

			//draw measurelines
			let topY = y + measure.extraMeasurelineTop;
			let botY = lineY + bottom - measure.extraMeasurelineBot;
			_.each(info.measurelines, (line, k) => {
				let tempY = botY;
				if (line.measureLineInfo.side === "top") {
					tempY = topY - (markSizes.small * 3) / 2;
					topY -= measureLineMeasures[2 * k + 1];
				} else {
					botY += measureLineMeasures[2 * k + 1];
				}
				const positions = _.map(measurelineXMeasures[k], e => e + x);
				context.save();
				context.strokeStyle = ColorUtil.translateToDiagramColor(
					line.measureLineInfo.color
				);
				context
					.bp()
					.M(positions[0], tempY + markSizes.small / 2)
					.H(_.last(positions))
					.stroke();
				_.each(positions, p =>
					context
						.bp()
						.M(p, tempY)
						.v(markSizes.small)
						.stroke()
				);
				context.restore();
				_.each(line.values, (e, j) => {
					const mLineLabelY =
						tempY +
						(line.measureLineInfo.side === "top"
							? -1 * measureLineMeasures[2 * k][j].bottom
							: markSizes.small + measureLineMeasures[2 * k][j].top);
					rc.drawNode(
						rc,
						(positions[j] + positions[j + 1]) / 2 -
							measureLineMeasures[2 * k][j].width / 2,
						mLineLabelY,
						e,
						measure
					);
				});
			});
			context.restore();

			// draw tick marks
			if (tickMeasures.length) {
				context.save();
				_.each(tickMeasures, (tick, key) => {
					const positions = [];
					const wholeIterations = Math.floor(
						tick.number.numerator / tick.number.denominator
					);

					let yPos = lineY - maxMarkSize / 2 - markSizes.large / 2;
					for (let i = 1; i <= wholeIterations; i++) {
						positions.push([tick.origin, yPos]);
						yPos -= tick.singleTickHeight + tick.tickPadding;
					}
					_.each(positions, position => {
						rc.drawNode(
							rc,
							position[0],
							position[1],
							info.ticks[key].symbolNode,
							tick.symbolNodeMeasure
						);
					});
				});
				context.restore();
			}
		},
		measure(rc, fontInfo, props, node) {
			const info = node.numberlineInfo;
			const fontSize = fontInfo.size;
			let width = fontSize * 20;
			if (info.size === "small") {
				width = fontSize * 10;
			} else if (info.size === "large" || info.size === "largepadding") {
				width = fontSize * 30;
			}
			const arrowEndWidth = fontSize;
			if (info.endArrow === "left" || info.endArrow === "right") {
				width += arrowEndWidth;
			} else if (info.endArrow === "both") {
				width += 2 * arrowEndWidth;
			}
			// Note: if largePadding is changed, be sure to adjust size in
			// KangaroosterNumber.getAdjustedRect
			const sidePadding =
				info.size === "largepadding" ? fontSize : fontSize / 2;
			if (props.maxLineWidth) {
				width = Math.min(props.maxLineWidth - 2 * sidePadding, width);
			}
			const markSizes = {
				tiny: fontSize / 3,
				small: fontSize / 2,
				medium: fontSize,
				large: (fontSize * 3) / 2,
				xlarge: fontSize * 2,
				huge: fontSize * 3,
			};
			let maxMarkSize = markSizes["small"];
			_.each(
				info.marks,
				e => (maxMarkSize = Math.max(maxMarkSize, markSizes[e.markInfo.size]))
			);

			let maxTopLabelTop = 0;
			let maxTopLabelBottom = 0;
			let maxBottomLabelTop = 0;
			let maxBottomLabelBottom = 0;
			let maxTopStackLabelTop = 0;
			let maxTopStackLabelBottom = 0;
			const smallLabelFontInfo = _.extend({}, fontInfo, {
				size: fontSize * 0.75,
			});
			const arrowHeight = markSizes.large;
			const arrowPadding = arrowHeight * 0.2;

			for (let i = 0; i < info.labels.length; i++) {
				const labelFontInfo =
					info.labels[i].labelInfo.size === "small"
						? smallLabelFontInfo
						: fontInfo;
				let labelHeight = 0;
				if (info.labels[i].labelInfo.arrow) {
					labelHeight = arrowHeight;
				}
				const labelArrowHeight =
					labelHeight + (info.labels[i].labelInfo.arrow ? arrowPadding : 0);
				const labelMeasure = rc.measureNode(
					rc,
					labelFontInfo,
					props,
					info.labels[i].labelNode
				);
				const labelSide = info.labels[i].labelInfo.side;
				if (labelSide === "top") {
					maxTopLabelTop = Math.max(
						maxTopLabelTop,
						labelMeasure.top + labelArrowHeight
					);
					maxTopLabelBottom = Math.max(maxTopLabelBottom, labelMeasure.bottom);
				} else if (labelSide === "stack") {
					maxTopStackLabelTop = Math.max(maxTopStackLabelTop, labelMeasure.top);
					maxTopStackLabelBottom = Math.max(
						maxTopStackLabelBottom,
						labelMeasure.bottom
					);
				} else {
					maxBottomLabelTop = Math.max(maxBottomLabelTop, labelMeasure.top);
					maxBottomLabelBottom = Math.max(
						maxBottomLabelBottom,
						labelMeasure.bottom + labelArrowHeight
					);
				}
			}

			let extraMeasurelineTop = 0;
			let extraMeasurelineBot = 0;
			const measureLineMeasures = [];
			// const measureLineFontInfo = fontInfo;//_.extend({}, fontInfo, {size: fontSize / 2});
			_.each(info.measurelines, line => {
				measureLineMeasures.push(
					_.map(line.values, e => rc.measureNode(rc, fontInfo, props, e))
				);
				const size =
					markSizes.small +
					_.max(_.map(_.last(measureLineMeasures), e => e.top + e.bottom));
				measureLineMeasures.push(size);
				if (line.measureLineInfo.side === "top") {
					extraMeasurelineTop += size;
				} else {
					extraMeasurelineBot += size;
				}
			});

			const arrowLabelMeasures = _.map(info.arrows, arr =>
				arr.label ? rc.measureNode(rc, fontInfo, props, arr.label) : null
			);
			const arrLabelPadding = 5;
			let maxArrTop = 0;
			let maxArrBot = 0;
			const toAdd = [];
			let miniTopArrowChange = 0;
			let miniBottomArrowChange = 0;
			_.each(info.arrows, (arr, i) => {
				let currToAdd = 0;
				let s = markSizes[arr.size] / 2 + getLineWidth(rc, fontInfo) / 2;
				s += arr.label
					? arrLabelPadding +
					  arrowLabelMeasures[i].top +
					  arrowLabelMeasures[i].bottom
					: 0;
				if (arr.farOut && arr.farOut !== "low") {
					currToAdd = maxMarkSize / 2;
					if (arr.farOut === "high") {
						const drawArrowPadding = 2 * getLineWidth(rc, fontInfo);
						if (arr.side === "bottom") {
							currToAdd +=
								maxBottomLabelTop + maxBottomLabelBottom
									? maxBottomLabelTop +
									  maxBottomLabelBottom -
									  drawArrowPadding +
									  2 * drawArrowPadding
									: 0;
							miniBottomArrowChange = currToAdd ? 2 * drawArrowPadding : 0;
						} else {
							currToAdd +=
								maxTopLabelBottom + maxTopLabelTop
									? maxTopLabelBottom +
									  maxTopLabelTop -
									  drawArrowPadding +
									  3 * drawArrowPadding
									: 0;
							miniTopArrowChange = currToAdd ? 2 * drawArrowPadding : 0;
						}
						s +=
							arr.side === "top"
								? maxTopLabelTop + maxTopLabelBottom
								: maxBottomLabelTop + maxBottomLabelBottom;
					}
				} else if (arr.farOut === "low") {
					s -= maxMarkSize / 2;
				}
				if (arr.side === "top") {
					currToAdd *= -1;
					maxArrTop = Math.max(maxArrTop, s);
				} else {
					maxArrBot = Math.max(maxArrBot, s);
				}
				toAdd.push(currToAdd);
			});
			// Sizing the xs and checks commands
			const xcheckHeight = fontSize;
			const xWidth = rc.context.measureText("\u2717").width;
			const checkWidth = rc.context.measureText("\u2713").width;
			// Consider big arrows on the top and bottom for maxTop and maxBottom
			// Also consider the xs and checks for maxBottom
			// If fixed is true, the numberline will always be a fixed top height.
			// This guarantees the line itself is in same position across different numberlines.
			// (Used in engines with numberlines, e.g. kangarooster.)
			const hasXOrCheck = info.xs.length || info.checks.length;
			const extraHeight = hasXOrCheck ? xcheckHeight : 0;
			const hasTopBigArrow = _.find(info.bigArrows, {side: "top"});
			const tickMeasures = _.map(info.ticks, tick => {
				const symbolFontInfo = _.clone(fontInfo);
				symbolFontInfo.size *= tick.scale;
				const symbolNodeMeasure = rc.measureNode(
					rc,
					symbolFontInfo,
					props,
					tick.symbolNode
				);
				const singleTickHeight = symbolNodeMeasure.top * 2;
				const numberOfTicks =
					tick.number?.numerator / tick.number?.denominator || 0;
				const tickPadding = 0; // TODO: Make configurable
				const stackHeight =
					singleTickHeight * numberOfTicks + tickPadding * numberOfTicks;
				return {
					origin: 0, // This has to be calculated later when the number placements are calculated
					singleTickHeight,
					number: tick.number,
					style: tick.style,
					stackHeight,
					position: tick.position,
					symbolNodeMeasure,
					tickPadding,
				};
			});

			const tallestTickStack = _.max(_.map(tickMeasures, "stackHeight")) || 0;
			const maxContentTop =
				Math.max(
					maxTopLabelTop +
						maxTopLabelBottom +
						maxTopStackLabelTop +
						maxTopStackLabelBottom,
					hasTopBigArrow ? arrowHeight : 0,
					extraHeight,
					maxArrTop
				) +
				extraMeasurelineTop +
				tallestTickStack;

			const maxTop = info.fixed
				? fontSize * 3.5
				: maxMarkSize / 2 + maxContentTop;
			const hasBottomBigArrow = _.find(info.bigArrows, {side: "bottom"});
			const maxContentBottom =
				Math.max(
					maxBottomLabelTop + maxBottomLabelBottom,
					hasBottomBigArrow ? arrowHeight : 0,
					maxArrBot
				) + extraMeasurelineBot;

			const maxBottom = maxMarkSize / 2 + maxContentBottom;
			let lineLength =
				info.endArrow === "left" ||
				info.endArrow === "right" ||
				info.endArrow === "both"
					? arrowEndWidth
					: 0;
			lineLength =
				width - (info.endArrow === "both" ? 2 * lineLength : lineLength);
			const start =
				sidePadding +
				(info.endArrow === "left" || info.endArrow === "both"
					? arrowEndWidth
					: 0);
			const getFracX = frac =>
				start +
				(lineLength *
					Fractions.calcNumber(
						Fractions.subtractImproperFractions(frac, info.range[0])
					)) /
					Fractions.calcNumber(
						Fractions.subtractImproperFractions(info.range[1], info.range[0])
					);

			const markMeasures = _.map(info.marks, e => getFracX(e.position));
			const dotMeasures = _.map(info.dots, e => getFracX(e.position));
			const shadingMeasures = _.map(info.shading, e =>
				e.positions.map(getFracX)
			);
			const arrowMeasures = _.map(info.arrows, e => e.positions.map(getFracX));
			const labelMeasures = _.map(info.labels, e => {
				const temp = rc.getMeasure(e.labelNode).width / 2;
				return [getFracX(e.position) - temp, temp];
			});
			const bigArrowMeasures = _.map(info.bigArrows, e => getFracX(e.position));
			const xMeasures = _.map(info.xs, e => getFracX(e.position) - xWidth / 2);
			const checkMeasures = _.map(
				info.checks,
				e => getFracX(e.position) - checkWidth / 2
			);
			const measurelineXMeasures = _.map(info.measurelines, e =>
				e.positions.map(getFracX)
			);

			return {
				width: width + sidePadding * 2,
				height: maxTop + maxBottom + miniTopArrowChange + miniBottomArrowChange,
				top: maxTop + miniTopArrowChange,
				bottom: maxBottom + miniBottomArrowChange,
				fontInfo,
				maxTopLabelBottom,
				maxTopLabelHeight: maxTopLabelBottom + maxTopLabelTop,
				maxBottomLabelTop,
				maxBottomLabelHeight: maxBottomLabelTop + maxBottomLabelBottom,
				maxTopStackLabelBottom,
				maxTopStackLabelHeight: maxTopStackLabelTop + maxTopStackLabelBottom,
				arrowEndWidth,
				markSizes,
				arrowHeight,
				arrowPadding,
				xcheckHeight,
				xWidth,
				checkWidth,
				sidePadding,
				measureLineMeasures,
				extraMeasurelineBot,
				extraMeasurelineTop,
				arrowLabelMeasures,
				arrLabelPadding,
				maxMarkSize,
				markMeasures,
				dotMeasures,
				shadingMeasures,
				arrowMeasures,
				labelMeasures,
				bigArrowMeasures,
				xMeasures,
				checkMeasures,
				measurelineXMeasures,
				toAdd,
				tickMeasures: _.map(tickMeasures, tick => {
					return {
						...tick,
						origin: getFracX(tick.position) - tick.symbolNodeMeasure.width / 2,
					};
				}),
			};
		},
	},
	blockDiagram: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {diagramCanvas, diagramWidth, diagramHeight, blanks} = drawDiagram(
				rc,
				measure.fontInfo,
				measure.props,
				node
			);

			context.save();

			const diagramX = Math.round(x);
			const diagramY = Math.round(y);

			const currentTransform = context.getTransform();

			// Make the ratio rounded to nearest pixel, so that 0.999 rounds to 1.0 if it doesn't actually change the size
			const newWidth = Math.round((currentTransform[0] || 1) * diagramWidth);
			const newHeight = Math.round((currentTransform[3] || 1) * diagramHeight);
			context.setTransform(
				newWidth / diagramWidth,
				currentTransform[1],
				currentTransform[2],
				newHeight / diagramHeight,
				Math.round(currentTransform[4]) || 0,
				Math.round(currentTransform[5]) || 0
			);

			context.drawImage(diagramCanvas, diagramX, diagramY);

			const dBlanks = blanks.map(b => {
				const dBlank = _.extend(_.cloneDeep(b), {
					x: b.x + diagramX,
					y: b.y + diagramY,
				});
				if (b.type === "fraction" || b.type === "mixed") {
					if (dBlank.typeInfo.numerator) {
						dBlank.typeInfo.numerator.x += diagramX;
						dBlank.typeInfo.numerator.y += diagramY;
					}
					if (dBlank.typeInfo.denominator) {
						dBlank.typeInfo.denominator.x += diagramX;
						dBlank.typeInfo.denominator.y += diagramY;
					}
				}
				if (b.type === "mixed" && dBlank.typeInfo.whole) {
					dBlank.typeInfo.whole.x += diagramX;
					dBlank.typeInfo.whole.y += diagramY;
				}
				if (dBlank.naturalLineBlank) {
					dBlank.naturalLineBlank.x += diagramX;
					dBlank.naturalLineBlank.y += diagramY;
				}
				return dBlank;
			});
			for (let i = 0; i < dBlanks.length; i++) {
				rc.pushBlank(dBlanks[i]);
			}

			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const diagramData = drawDiagram(rc, fontInfo, props, node);
			return {
				width: diagramData.diagramWidth,
				height: diagramData.diagramHeight,
				top: diagramData.diagramHeight / 2,
				bottom: diagramData.diagramHeight / 2,
				maxLineWidth: node.maxLineWidth,
				fontInfo,
				// extra
				props,
				diagramBlanks: diagramData.blanks,
			};
		},
	},
};

module.exports = NodesEnvironments;
